import {compact, partition, take, takeRight} from 'lodash';
import React, {FC, PropsWithChildren, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  buildCalendarEventVersionPublicId,
  isEventEditable,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {useClipboard} from '../../../helpers/hooks/clipboardHookHelpers';
import {isRecurringSaveTypeRequired} from '../../../helpers/models/recurringEventHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {CalendarEventStatusesEnum} from '../../../models/api/calendarEventModel';
import {CalendarEventVersionVisualStatusesEnum} from '../../../models/api/calendarEventVersionModel';
import {CalendarProviderTypesEnum} from '../../../models/api/calendarModel';
import {
  CalendarModalActionsEnum,
  CalendarModalTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../services/tracking';
import {RecurringEventSaveTypesEnum} from '../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {getHasNotifyCalendarGuests} from '../../../store/featureFlags/featureFlagsSelectors';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {computeNonResourceGuestCount} from '../calendarEventHelpers';
import {
  CalendarEventViewerHeaderMoreButton,
  EventViewerButtonDefinition,
} from './calendarEventViewerHeaderMoreButton';
import {DeleteCalendarEventDialog} from './delete/deleteCalendarEventDialog';

export type DeleteConfirmHandler = (
  recurringSaveType?: RecurringEventSaveTypesEnum,
  shouldNotifyGuests?: boolean,
) => void;

/*
 * Props.
 */

export interface CalendarEventViewerHeaderButtonsProps {
  calendarEventData: CalendarEventData;
  onEditClick: Handler;
  onDeleteClick: DeleteConfirmHandler;
  onEmailParticipantsClick: Handler;
  numberOfPrimaryButtons: number;
  shouldHandleKeyboardShortcuts?: boolean;
  onRequestClose?: Handler;
}

/*
 * Style.
 */

const StyledButtonWrapperDiv = styled.div`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  grid-column-gap: 6px;
`;

/*
 * Component.
 */

export const CalendarEventViewerHeaderButtons: FC<
  PropsWithChildren<CalendarEventViewerHeaderButtonsProps>
> = (props) => {
  const theme = useUserTheme();
  const hasNotifyGuests = useAppSelector(getHasNotifyCalendarGuests);

  const {calendarEventData, onEditClick, onDeleteClick, onEmailParticipantsClick} = props;
  const {calendar, calendarEvent, calendarEventVersion, calendarVersion} = calendarEventData;
  const canEdit = isEventEditable(calendar, calendarVersion, calendarEventVersion);

  const {visualStatus} = calendarEventVersion;
  const isCalendarEventVersionIncomplete = visualStatus !== CalendarEventVersionVisualStatusesEnum.COMPLETE;

  const [shouldNotifyGuests, toggleShouldNotifyGuests] = useToggle(true);

  /*
   * Event handlers.
   */

  const [isDeleteConfirmationOpen, toggleIsDeleteConfirmationOpen] = useToggle();
  const onConfirmDeleteClick = useCallback(
    (recurringSaveType?: RecurringEventSaveTypesEnum) => {
      onDeleteClick(recurringSaveType, shouldNotifyGuests);
      // Redundant tracking but necessary due to downstream dependencies. Other event trackings rely on this event being tracked.
      // TODO: Coordinate effort with data team to eliminate redundancy.
      trackInterfaceEvent(InterfaceEventsEnum.DELETE_CALENDAR_EVENT);
      trackInterfaceEvent(InterfaceEventsEnum.CALENDAR_EVENT_MODAL_CLICK, {
        calendar_event_uid: calendarEventData.calendarEvent.uid,
        modal_type: CalendarModalTypesEnum.DELETE,
        action: CalendarModalActionsEnum.CONFIRM,
        notify_attendees: shouldNotifyGuests,
        calendar_provider: calendarEventData.calendar.provider,
      });
    },
    [
      calendarEventData.calendar.provider,
      calendarEventData.calendarEvent.uid,
      onDeleteClick,
      shouldNotifyGuests,
    ],
  );

  const {copy} = useClipboard();
  const onCopyEventIdSelect = useCallback(() => {
    if (calendarEventVersion.visualStatus === CalendarEventVersionVisualStatusesEnum.DRAFT) {
      return;
    }

    copy(buildCalendarEventVersionPublicId(calendarEventVersion));
  }, [calendarEventVersion, copy]);

  /*
   * Button definitions.
   */

  const {numberOfPrimaryButtons, children} = props;
  const {primaryButtons, moreDropdownButtons} = useMemo(() => {
    const buttons = compact<EventViewerButtonDefinition>([
      canEdit &&
        calendarEvent.status !== CalendarEventStatusesEnum.CANCELLED && {
          id: 'edit',
          icon: 'edit',
          title: <Trans id="eventviewer-header-edit">Edit</Trans>,
          isDisabled: isCalendarEventVersionIncomplete,
          isDropdownOnly: false,
          onClick: onEditClick,
          keyboardAction: props.shouldHandleKeyboardShortcuts
            ? KeyboardActionsEnum.CALENDAR_EDIT_EVENT
            : undefined,
        },
      canEdit && {
        id: 'delete',
        icon: 'trash',
        title: <Trans id="eventviewer-header-delete">Delete event</Trans>,
        isDisabled: isCalendarEventVersionIncomplete,
        isDropdownOnly: false,
        onClick: toggleIsDeleteConfirmationOpen,
        keyboardAction: props.shouldHandleKeyboardShortcuts
          ? KeyboardActionsEnum.CALENDAR_DELETE_EVENT
          : undefined,
      },
      {
        id: 'email',
        icon: 'message',
        title: <Trans id="eventviewer-header-email">Email guests</Trans>,
        isDisabled: isCalendarEventVersionIncomplete,
        isDropdownOnly: false,
        onClick: onEmailParticipantsClick,
      },
      {
        id: 'copyId',
        icon: 'copy',
        title: <Trans id="eventviewer-copyid">Copy event ID</Trans>,
        isDisabled: isCalendarEventVersionIncomplete,
        isDropdownOnly: true,
        onClick: onCopyEventIdSelect,
      },
    ]);
    const [iconButtons, dropdownOnlyButtons] = partition(buttons, (b) => !b.isDropdownOnly);

    const hasButtonsOverflow = numberOfPrimaryButtons < iconButtons.length || dropdownOnlyButtons.length > 0;
    const numberOfVisiblePrimaryButtons = hasButtonsOverflow
      ? numberOfPrimaryButtons - 1
      : numberOfPrimaryButtons;
    const overflowButtons = takeRight(iconButtons, iconButtons.length - numberOfVisiblePrimaryButtons);

    return {
      primaryButtons: take(iconButtons, numberOfVisiblePrimaryButtons),
      moreDropdownButtons: [...overflowButtons, ...dropdownOnlyButtons],
    };
  }, [
    calendarEvent.status,
    canEdit,
    isCalendarEventVersionIncomplete,
    numberOfPrimaryButtons,
    onCopyEventIdSelect,
    onEditClick,
    onEmailParticipantsClick,
    props.shouldHandleKeyboardShortcuts,
    toggleIsDeleteConfirmationOpen,
  ]);

  /*
   * Render.
   */

  const renderDeleteConfirmation = () => {
    const nonResourceGuestCount = computeNonResourceGuestCount(calendarEvent.participants);
    const onRequestClose = () => {
      toggleIsDeleteConfirmationOpen();
      trackInterfaceEvent(InterfaceEventsEnum.CALENDAR_EVENT_MODAL_CLICK, {
        calendar_event_uid: calendarEventData.calendarEvent.uid,
        modal_type: CalendarModalTypesEnum.DELETE,
        action: CalendarModalActionsEnum.CANCEL,
        notify_attendees: shouldNotifyGuests,
        calendar_provider: calendarEventData.calendar.provider,
      });
    };
    return (
      <DeleteCalendarEventDialog
        isRecurring={isRecurringSaveTypeRequired(calendarEvent)}
        onConfirmClick={onConfirmDeleteClick}
        onRequestClose={onRequestClose}
        shouldShowNotifyGuestsOption={
          calendarEventVersion.isOrganizerCopy &&
          nonResourceGuestCount > 0 &&
          hasNotifyGuests &&
          calendar.provider === CalendarProviderTypesEnum.GOOGLE
        }
        toggleShouldNotifyGuests={toggleShouldNotifyGuests}
        shouldNotifyGuests={shouldNotifyGuests}
        nonResourceGuestCount={nonResourceGuestCount}
      />
    );
  };

  const renderButton: RendererOf<EventViewerButtonDefinition> = (button) => (
    <IconButton
      key={button.id}
      iconName={button.icon}
      label={button.title}
      isDisabled={button.isDisabled}
      onClick={button.onClick}
      keyboardAction={button.keyboardAction}
      color={theme.greys.shade80}
      hoverColor={theme.greys.shade80}
    />
  );

  const hasMoreDropdownButtons = moreDropdownButtons.length > 0;
  return (
    <>
      <StyledButtonWrapperDiv>
        {primaryButtons.map(renderButton)}
        {hasMoreDropdownButtons && <CalendarEventViewerHeaderMoreButton buttons={moreDropdownButtons} />}
        {children}
      </StyledButtonWrapperDiv>
      {isDeleteConfirmationOpen && renderDeleteConfirmation()}
    </>
  );
};
